import { useRef, useState, useCallback } from "react";
import { MoveHorizontal } from "lucide-react";

export default function BeforeAfterSlider({ before, after, beforeLabel = "Előtte", afterLabel = "Utána" }) {
  const containerRef = useRef(null);
  const [pos, setPos] = useState(50);
  const dragging = useRef(false);

  const update = useCallback((clientX) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.max(0, Math.min(100, x)));
  }, []);

  const onMove = useCallback((e) => {
    if (!dragging.current) return;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    update(clientX);
  }, [update]);

  const stop = useCallback(() => {
    dragging.current = false;
    window.removeEventListener("mousemove", onMove);
    window.removeEventListener("mouseup", stop);
    window.removeEventListener("touchmove", onMove);
    window.removeEventListener("touchend", stop);
  }, [onMove]);

  const start = useCallback((e) => {
    dragging.current = true;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    update(clientX);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", stop);
    window.addEventListener("touchmove", onMove, { passive: true });
    window.addEventListener("touchend", stop);
  }, [onMove, stop, update]);

  return (
    <div
      ref={containerRef}
      data-testid="before-after-slider"
      className="relative aspect-[4/3] w-full select-none overflow-hidden rounded-[2rem] shadow-2xl shadow-black/30 cursor-ew-resize"
      onMouseDown={start}
      onTouchStart={start}
    >
      <img src={after} alt={afterLabel} className="absolute inset-0 h-full w-full object-cover" draggable="false" />
      <span className="absolute top-4 right-4 z-10 rounded-full bg-[#0E5A75] px-4 py-1.5 text-xs font-semibold text-white shadow-lg">{afterLabel}</span>

      <img
        src={before}
        alt={beforeLabel}
        className="absolute inset-0 h-full w-full object-cover"
        style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}
        draggable="false"
      />
      <span className="absolute top-4 left-4 z-10 rounded-full bg-[#0E5A75] px-4 py-1.5 text-xs font-semibold text-white shadow-lg" style={{ opacity: pos > 14 ? 1 : 0 }}>{beforeLabel}</span>

      <div className="absolute top-0 bottom-0 z-20 w-1 bg-white shadow-[0_0_0_1px_rgba(0,0,0,0.1)]" style={{ left: `${pos}%`, transform: "translateX(-50%)" }}>
        <div
          data-testid="slider-handle"
          className="absolute top-1/2 left-1/2 flex h-12 w-12 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white text-[#0E5A75] shadow-lg"
        >
          <MoveHorizontal className="h-5 w-5" />
        </div>
      </div>
    </div>
  );
}
